import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";

type Action = { tool: string; summary: string; payload: Record<string, any> };
type ReviewData = {
  approval_id: string; title: string; status: string; method: string;
  originated_from_untrusted: boolean; actions: Action[];
};

export default function Review() {
  const { token } = useParams();
  const [data, setData] = useState<ReviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    api.get(`/review/${token}`).then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));
  }, [token]);

  const act = async (kind: "approve" | "reject") => {
    setBusy(true);
    try {
      const r = await api.post(`/review/${token}/${kind}`);
      setDone(kind === "approve"
        ? (r.data.all_verified ? "Approved — all actions executed and verified (simulated)."
          : "Approved — executed, but some actions failed verification.")
        : "Rejected — nothing was executed.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (error) return (
    <div className="rounded-3xl border border-border bg-card p-8 text-center" data-testid="review-error">
      <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
      <p className="mt-3 text-sm">{error}</p>
    </div>
  );
  if (!data) return <p className="text-sm text-muted-foreground" data-testid="review-loading">Loading review…</p>;

  return (
    <div data-testid="review-page">
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <h1 className="font-display text-2xl font-semibold tracking-tight">Secure review</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Review exactly what will happen. Every action below is <strong>simulated</strong>.
      </p>

      {data.originated_from_untrusted && (
        <div className="mt-3 flex items-center gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-600" data-testid="review-untrusted-flag">
          <AlertTriangle className="h-4 w-4" /> Prepared from external (untrusted) content — check carefully.
        </div>
      )}

      {done ? (
        <div className="mt-6 rounded-2xl border border-border bg-card p-6 text-center" data-testid="review-result">
          <Check className="mx-auto h-8 w-8 text-emerald-500" />
          <p className="mt-3 text-sm">{done}</p>
        </div>
      ) : (
        <>
          <div className="mt-5 space-y-3">
            {data.actions.map((a, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-4" data-testid={`review-action-${i}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">{a.tool}</div>
                <p className="mt-1 text-sm font-medium">{a.summary}</p>
                <pre className="mt-2 overflow-x-auto rounded-xl bg-secondary/50 p-3 text-xs">
                  {JSON.stringify(a.payload, null, 2)}
                </pre>
              </div>
            ))}
          </div>

          {data.status !== "pending" ? (
            <p className="mt-6 text-center text-sm text-muted-foreground" data-testid="review-settled">
              This request is already {data.status}.
            </p>
          ) : (
            <div className="mt-6 flex gap-3">
              <Button className="flex-1 rounded-full" disabled={busy} data-testid="review-approve"
                onClick={() => act("approve")}>
                <Check className="mr-1 h-4 w-4" /> Approve all
              </Button>
              <Button variant="secondary" className="flex-1 rounded-full" disabled={busy}
                data-testid="review-reject" onClick={() => act("reject")}>
                <X className="mr-1 h-4 w-4" /> Reject
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
